import { useRef, useEffect, useCallback, useState } from "react";
import { FaceLandmarker, PoseLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";

type VideoRef = React.RefObject<HTMLVideoElement>;

export type HeadPose = "front" | "left" | "right" | "unknown";

// ── Configuration constants ─────────────────────────────────────────────────
/** Exponential smoothing factor (0–1). Higher = more responsive, lower = smoother */
const YAW_SMOOTHING = 0.8;
/** Yaw threshold (degrees) to classify as left/right vs front */
const YAW_THRESHOLD = 18;
/** Face considered "present" even when briefly lost, up to this many ms */
const FACE_DEBOUNCE_MS = 1000;
/** Trigger "looking_away" flag after this many ms of non-front pose */
const LOOKING_AWAY_THRESHOLD_MS = 5000; // Increased from 2000ms
/** Trigger "no_face" flag after this many ms without a face */
const NO_FACE_THRESHOLD_MS = 2000;
/** Target detection FPS — throttles the loop to save CPU */
const TARGET_FPS = 10;
const FRAME_INTERVAL_MS = 1000 / TARGET_FPS;
/** Minimum average brightness (0–255) required for a good capture */
const MIN_BRIGHTNESS = 50;
/** Minimum image variance (0–1) to detect obstruction */
const MIN_VARIANCE = 0.01;
/** Background motion threshold to flag movement (0–100) */
const MOTION_THRESHOLD = 15;
/** Face occlusion threshold to flag coverage (0–100) */
const OCCLUSION_THRESHOLD = 30;
/** JPEG quality for captures sent to Rekognition */
const CAPTURE_JPEG_QUALITY = 0.95;
/** Buffer frames required before triggering head movement warning */
const HEAD_MOVEMENT_BUFFER_FRAMES = 5;
/** Cooldown period between head movement warnings (ms) */
const HEAD_MOVEMENT_COOLDOWN_MS = 10000;

export interface FaceStatus {
  count: number;
  detected: boolean;
  /** Estimated head orientation based on smoothed yaw */
  pose: HeadPose;
  /** Smoothed yaw angle in degrees (+ve = person turned to their right) */
  yawDeg: number;
  /** Canvas with landmark overlay drawn on it (updated each frame) */
  landmarkCanvas: HTMLCanvasElement | null;
  /** Active proctoring flag names */
  flags: string[];
  /** Attention score 0–100 (100 = fully attentive) */
  attentionScore: number;
  /** Timestamp (ms) the face was last seen */
  lastSeenAt: number;
  /** Cumulative ms the user has been looking away from "front" */
  lookAwayDuration: number;
  /** Cumulative ms with no face detected */
  noFaceDuration: number;
  /** Background motion level (0–100) */
  backgroundMotion: number;
  /** Image variance (0–1, low = obstructed/covered) */
  imageVariance: number;
  /** Face occlusion percentage (0–100) */
  faceOcclusion: number;
  /** Number of people/bodies detected via pose landmarker */
  personCount: number;
  /** Blink count in current 60-second window */
  blinkCount: number;
  /** Timestamp of last detected blink */
  lastBlinkAt: number;
  /** Current Eye Aspect Ratio (0-1, lower = more closed) */
  eyeAspectRatio: number;
  /** Spoof confidence based on liveness checks */
  spoofConfidence: "low" | "medium" | "high";
  /** Whether camera is blocked/covered */
  cameraBlocked: boolean;
}

/** Reason a capture was rejected */
export type CaptureRejectReason =
  | "no_face"
  | "multiple_faces"
  | "too_dark"
  | "not_ready"
  | null;

export interface CaptureResult {
  blob: Blob | null;
  faceCount: number;
  /** If blob is null and faceCount isn't the issue, this explains why */
  rejectReason: CaptureRejectReason;
}

// ── Yaw computation ─────────────────────────────────────────────────────────
/**
 * Compute raw head yaw from FaceLandmarker NormalizedLandmarks.
 *
 * Key landmark indices (MediaPipe 478-point canonical face mesh):
 *   1  = nose tip
 *   234 = left cheek / left temple
 *   454 = right cheek / right temple
 *
 * The webcam feed is mirrored, so a sign correction is applied:
 *   positive yaw → person turned to THEIR right
 *   negative yaw → person turned to THEIR left
 */
function computeRawYaw(landmarks: { x: number; y: number; z: number }[]): number {
  if (landmarks.length < 455) return 0;

  const leftEar = landmarks[234];
  const rightEar = landmarks[454];
  const nose = landmarks[1];

  const midX = (leftEar.x + rightEar.x) / 2;
  const earSpan = rightEar.x - leftEar.x;

  if (earSpan < 0.001) return 0;

  const rawOffset = (nose.x - midX) / earSpan;
  // Negate for mirror correction and scale to approximate degrees
  return -rawOffset * 90;
}

// ── Frame analysis ──────────────────────────────────────────────────────────
/**
 * Analyze video frame for brightness, variance, and motion.
 * Samples a central region for performance.
 */
interface FrameAnalysis {
  brightness: number;    // 0–255
  variance: number;      // 0–1 (normalized)
  motionLevel: number;   // 0–100
}

function analyzeFrame(
  video: HTMLVideoElement,
  prevFrameData: ImageData | null,
  canvas: HTMLCanvasElement
): FrameAnalysis {
  const sampleSize = 64;
  canvas.width = sampleSize;
  canvas.height = sampleSize;
  const ctx = canvas.getContext("2d", { alpha: false, desynchronized: true });

  if (!ctx) {
    return { brightness: 255, variance: 1, motionLevel: 0 };
  }

  const sx = (video.videoWidth - sampleSize) / 2;
  const sy = (video.videoHeight - sampleSize) / 2;
  ctx.drawImage(video, sx, sy, sampleSize, sampleSize, 0, 0, sampleSize, sampleSize);

  const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize);
  const { data } = imageData;

  // Compute brightness and variance
  let sum = 0;
  let sumSq = 0;
  const pixelCount = sampleSize * sampleSize;

  for (let i = 0; i < data.length; i += 4) {
    const luminance = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
    sum += luminance;
    sumSq += luminance * luminance;
  }

  const brightness = sum / pixelCount;
  const mean = brightness;
  const variance = Math.sqrt(sumSq / pixelCount - mean * mean) / 255; // Normalize 0–1

  // Motion detection via frame differencing
  let motionLevel = 0;
  if (prevFrameData) {
    let diffSum = 0;
    for (let i = 0; i < data.length; i += 4) {
      const currLum = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      const prevLum = prevFrameData.data[i] * 0.299 + prevFrameData.data[i + 1] * 0.587 + prevFrameData.data[i + 2] * 0.114;
      diffSum += Math.abs(currLum - prevLum);
    }
    motionLevel = Math.min(100, (diffSum / pixelCount / 255) * 100);
  }

  return { brightness, variance, motionLevel };
}

// ── Face occlusion detection ────────────────────────────────────────────────
/**
 * Estimate face occlusion by checking landmark visibility and distribution.
 * Returns percentage 0–100 (higher = more occluded).
 */
function estimateFaceOcclusion(landmarks: { x: number; y: number; z: number }[]): number {
  if (landmarks.length < 468) return 0;

  // Key regions to check: eyes, nose, mouth, cheeks
  const keyPoints = [
    33, 263,   // eyes
    1, 4,      // nose
    61, 291,   // mouth corners
    234, 454,  // cheeks
    10, 152    // forehead, chin
  ];

  // Check z-depth variance (occluded faces have irregular z-depth)
  let zSum = 0;
  let zSumSq = 0;
  for (const idx of keyPoints) {
    if (landmarks[idx]) {
      const z = landmarks[idx].z;
      zSum += z;
      zSumSq += z * z;
    }
  }
  const zMean = zSum / keyPoints.length;
  const zStdDev = Math.sqrt(zSumSq / keyPoints.length - zMean * zMean);

  // High z-variance suggests partial occlusion (some parts closer than others)
  const occlusionScore = Math.min(100, zStdDev * 5000); // Scale factor tuned empirically

  return occlusionScore;
}

// ── Background motion analysis ──────────────────────────────────────────────
/**
 * Analyze motion in non-face regions to detect background activity.
 */
function analyzeBackgroundMotion(
  video: HTMLVideoElement,
  prevFrameData: ImageData | null,
  faceLandmarks: { x: number; y: number; z: number }[] | null,
  canvas: HTMLCanvasElement
): number {
  if (!prevFrameData) return 0;

  const w = 160;  // Fixed lower resolution
  const h = 120;
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { alpha: false, desynchronized: true });
  if (!ctx) return 0;

  ctx.drawImage(video, 0, 0, w, h);
  const imageData = ctx.getImageData(0, 0, w, h);
  const { data } = imageData;

  // Mark face region indices (bounding box with margin)
  let fx1 = -1, fx2 = -1, fy1 = -1, fy2 = -1;
  if (faceLandmarks && faceLandmarks.length > 0) {
    let minX = 1, maxX = 0, minY = 1, maxY = 0;
    for (let i = 0; i < faceLandmarks.length; i++) {
      const lm = faceLandmarks[i];
      if (lm.x < minX) minX = lm.x;
      if (lm.x > maxX) maxX = lm.x;
      if (lm.y < minY) minY = lm.y;
      if (lm.y > maxY) maxY = lm.y;
    }

    const margin = 0.1;
    fx1 = Math.floor(Math.max(0, (minX - margin) * w));
    fx2 = Math.ceil(Math.min(w, (maxX + margin) * w));
    fy1 = Math.floor(Math.max(0, (minY - margin) * h));
    fy2 = Math.ceil(Math.min(h, (maxY + margin) * h));
  }

  // Compute motion only in background regions
  let bgDiffSum = 0;
  let bgPixelCount = 0;

  for (let y = 0; y < h; y++) {
    const isYInFace = y >= fy1 && y < fy2;
    for (let x = 0; x < w; x++) {
      // Skip face region WITHOUT boolean[][] allocation
      if (isYInFace && x >= fx1 && x < fx2) continue;

      const i = (y * w + x) * 4;
      const currLum = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      const prevLum = prevFrameData.data[i] * 0.299 + prevFrameData.data[i + 1] * 0.587 + prevFrameData.data[i + 2] * 0.114;
      bgDiffSum += Math.abs(currLum - prevLum);
      bgPixelCount++;
    }
  }

  if (bgPixelCount === 0) return 0;
  return Math.min(100, (bgDiffSum / bgPixelCount / 255) * 100);
}

// ── Spoof detection (Laplacian variance & color uniformity) ────────────────
/**
 * Check for spoofing/liveness by analyzing face texture and color variance.
 * A printed photo or phone screen will have unnaturally low variance.
 */
function checkForSpoofing(
  video: HTMLVideoElement,
  faceLandmarks: { x: number; y: number; z: number }[],
  canvas: HTMLCanvasElement
): { isSpoofed: boolean; confidence: "low" | "medium" | "high" } {
  // Calculate face bounding box
  let minX = 1, maxX = 0, minY = 1, maxY = 0;
  for (const lm of faceLandmarks) {
    minX = Math.min(minX, lm.x);
    maxX = Math.max(maxX, lm.x);
    minY = Math.min(minY, lm.y);
    maxY = Math.max(maxY, lm.y);
  }

  const vw = video.videoWidth;
  const vh = video.videoHeight;
  const faceX = Math.floor(minX * vw);
  const faceY = Math.floor(minY * vh);
  const faceW = Math.floor((maxX - minX) * vw);
  const faceH = Math.floor((maxY - minY) * vh);

  // Crop face to 64x64 canvas
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext("2d", { alpha: false, desynchronized: true }) as CanvasRenderingContext2D | null;
  if (!ctx) return { isSpoofed: false, confidence: "high" };

  ctx.drawImage(video, faceX, faceY, faceW, faceH, 0, 0, 64, 64);
  const imageData = ctx.getImageData(0, 0, 64, 64);
  const data = imageData.data;

  // Convert to grayscale
  const gray: number[] = [];
  for (let i = 0; i < data.length; i += 4) {
    const lum = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
    gray.push(lum);
  }

  // Compute Laplacian variance (measure of sharpness/texture)
  // Kernel: [[0,1,0],[1,-4,1],[0,1,0]]
  const laplacian: number[] = [];
  for (let y = 1; y < 63; y++) {
    for (let x = 1; x < 63; x++) {
      const idx = y * 64 + x;
      const lap =
        gray[idx - 64] +
        gray[idx - 1] +
        gray[idx + 1] +
        gray[idx + 64] -
        4 * gray[idx];
      laplacian.push(lap);
    }
  }

  const lapMean = laplacian.reduce((a, b) => a + b, 0) / laplacian.length;
  const lapVariance = laplacian.reduce((sum, val) => sum + (val - lapMean) ** 2, 0) / laplacian.length;

  // Compute per-channel RGB standard deviation
  const r: number[] = [];
  const g: number[] = [];
  const b: number[] = [];
  for (let i = 0; i < data.length; i += 4) {
    r.push(data[i]);
    g.push(data[i + 1]);
    b.push(data[i + 2]);
  }

  const meanR = r.reduce((a, b) => a + b, 0) / r.length;
  const meanG = g.reduce((a, b) => a + b, 0) / g.length;
  const meanB = b.reduce((a, b) => a + b, 0) / b.length;

  const stdR = Math.sqrt(r.reduce((sum, val) => sum + (val - meanR) ** 2, 0) / r.length);
  const stdG = Math.sqrt(g.reduce((sum, val) => sum + (val - meanG) ** 2, 0) / g.length);
  const stdB = Math.sqrt(b.reduce((sum, val) => sum + (val - meanB) ** 2, 0) / b.length);

  // Thresholds
  const LAPLACIAN_THRESHOLD = 120;
  const COLOR_STD_THRESHOLD = 15;

  const lowTexture = lapVariance < LAPLACIAN_THRESHOLD;
  const uniformColor = stdR < COLOR_STD_THRESHOLD && stdG < COLOR_STD_THRESHOLD && stdB < COLOR_STD_THRESHOLD;

  if (lowTexture && uniformColor) {
    return { isSpoofed: true, confidence: "low" };
  } else if (lowTexture || uniformColor) {
    return { isSpoofed: true, confidence: "medium" };
  } else {
    return { isSpoofed: false, confidence: "high" };
  }
}

// ── Eye blink detection (Eye Aspect Ratio) ─────────────────────────────────
/**
 * Calculate Eye Aspect Ratio (EAR) for blink detection.
 * EAR = (|p2-p6| + |p3-p5|) / (2 * |p1-p4|)
 * Lower EAR = more closed eyes
 */
function calculateEAR(eyeLandmarks: { x: number; y: number; z: number }[]): number {
  if (eyeLandmarks.length !== 6) return 1;

  const [p1, p2, p3, p4, p5, p6] = eyeLandmarks;

  const dist = (a: { x: number; y: number }, b: { x: number; y: number }) =>
    Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);

  const vertical1 = dist(p2, p6);
  const vertical2 = dist(p3, p5);
  const horizontal = dist(p1, p4);

  if (horizontal === 0) return 1;

  return (vertical1 + vertical2) / (2 * horizontal);
}

/**
 * Detect eye blinks using the EAR formula.
 * Returns current EAR and whether a blink was detected.
 * 
 * Note: Thresholds are adjusted to accommodate people wearing glasses,
 * which can obscure eye landmarks and affect EAR calculation accuracy.
 * The system is designed to be lenient to avoid false positives.
 */
function detectBlink(
  faceLandmarks: { x: number; y: number; z: number }[],
  earHistoryRef: React.MutableRefObject<number[]>,
  blinkCountRef: React.MutableRefObject<number>,
  lastBlinkAtRef: React.MutableRefObject<number>,
  blinkResetTimeRef: React.MutableRefObject<number>,
  now: number
): { ear: number; blinkDetected: boolean } {
  // Left eye: landmarks [33, 160, 158, 133, 153, 144]
  // Right eye: landmarks [362, 385, 387, 263, 373, 380]
  const leftEyeLandmarks = [
    faceLandmarks[33],
    faceLandmarks[160],
    faceLandmarks[158],
    faceLandmarks[133],
    faceLandmarks[153],
    faceLandmarks[144],
  ];

  const rightEyeLandmarks = [
    faceLandmarks[362],
    faceLandmarks[385],
    faceLandmarks[387],
    faceLandmarks[263],
    faceLandmarks[373],
    faceLandmarks[380],
  ];

  const leftEAR = calculateEAR(leftEyeLandmarks);
  const rightEAR = calculateEAR(rightEyeLandmarks);
  const avgEAR = (leftEAR + rightEAR) / 2;

  // Track EAR history (last 4 frames for blink detection)
  earHistoryRef.current.push(avgEAR);
  if (earHistoryRef.current.length > 4) {
    earHistoryRef.current.shift();
  }

  // Detect blink: EAR < 0.2 for 2-4 consecutive frames
  // Note: Threshold lowered to 0.18 to be more lenient for people wearing glasses
  const EAR_THRESHOLD = 0.18;
  const lowEarCount = earHistoryRef.current.filter(ear => ear < EAR_THRESHOLD).length;
  const blinkDetected = lowEarCount >= 2 && lowEarCount <= 4 && avgEAR >= EAR_THRESHOLD;

  if (blinkDetected) {
    blinkCountRef.current++;
    lastBlinkAtRef.current = now;
  }

  // Reset blink count every 60 seconds
  if (now - blinkResetTimeRef.current >= 60000) {
    blinkCountRef.current = 0;
    blinkResetTimeRef.current = now;
  }

  return { ear: avgEAR, blinkDetected };
}

// ── Camera block detection (pixel variance & frame hash) ───────────────────
/**
 * Compute a simple hash of pixel data for frame comparison.
 */
function computePixelHash(data: Uint8ClampedArray): string {
  let hash = 0;
  // Sample every 16th pixel for speed
  for (let i = 0; i < data.length; i += 64) {
    hash = ((hash << 5) - hash + data[i]) | 0;
  }
  return hash.toString(36);
}

/**
 * Check if camera is blocked by detecting low variance and static frames.
 */
function checkCameraBlock(
  video: HTMLVideoElement,
  brightness: number,
  faceCount: number,
  lastFrameHashRef: React.MutableRefObject<string>,
  staticFrameCountRef: React.MutableRefObject<number>,
  dt: number,
  canvas: HTMLCanvasElement
): { isBlocked: boolean; isStatic: boolean } {
  const MIN_BRIGHTNESS = 30; // From existing constant

  // Only check when no face detected
  if (faceCount > 0) {
    staticFrameCountRef.current = 0;
    return { isBlocked: false, isStatic: false };
  }

  // Sample center 32x32 pixels
  canvas.width = 32;
  canvas.height = 32;
  const ctx = canvas.getContext("2d", { alpha: false, desynchronized: true }) as CanvasRenderingContext2D | null;
  if (!ctx) return { isBlocked: false, isStatic: false };

  const sx = (video.videoWidth - 32) / 2;
  const sy = (video.videoHeight - 32) / 2;
  ctx.drawImage(video, sx, sy, 32, 32, 0, 0, 32, 32);
  const imageData = ctx.getImageData(0, 0, 32, 32);
  const data = imageData.data;

  // Compute color variance
  const pixels: number[][] = [];
  for (let i = 0; i < data.length; i += 4) {
    pixels.push([data[i], data[i + 1], data[i + 2]]);
  }

  const rValues = pixels.map(p => p[0]);
  const gValues = pixels.map(p => p[1]);
  const bValues = pixels.map(p => p[2]);

  const meanR = rValues.reduce((a, b) => a + b, 0) / rValues.length;
  const meanG = gValues.reduce((a, b) => a + b, 0) / gValues.length;
  const meanB = bValues.reduce((a, b) => a + b, 0) / bValues.length;

  const varR = rValues.reduce((sum, val) => sum + (val - meanR) ** 2, 0) / rValues.length;
  const varG = gValues.reduce((sum, val) => sum + (val - meanG) ** 2, 0) / gValues.length;
  const varB = bValues.reduce((sum, val) => sum + (val - meanB) ** 2, 0) / bValues.length;

  const avgVariance = (varR + varG + varB) / 3;
  const isBlocked = avgVariance < 5;

  // Check for static frame (>99% identical for 3 seconds)
  const currentHash = computePixelHash(data);
  const isIdentical = currentHash === lastFrameHashRef.current;

  if (isIdentical) {
    staticFrameCountRef.current += dt;
  } else {
    staticFrameCountRef.current = 0;
    lastFrameHashRef.current = currentHash;
  }

  const isStatic = staticFrameCountRef.current >= 3000;

  return { isBlocked, isStatic };
}

// ── Landmark drawing ────────────────────────────────────────────────────────
function drawLandmarks(
  canvas: HTMLCanvasElement,
  video: HTMLVideoElement,
  landmarks: { x: number; y: number; z: number }[],
  yawDeg: number,
  pose: HeadPose,
  poseLandmarks?: { x: number; y: number; z: number }[][]
) {
  if (!canvas || !video) return;
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext("2d") as CanvasRenderingContext2D | null;
  if (!ctx) return;

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Draw all landmark dots
  ctx.fillStyle = "rgba(0,255,100,0.7)";
  for (const lm of landmarks) {
    const x = lm.x * canvas.width;
    const y = lm.y * canvas.height;
    ctx.beginPath();
    ctx.arc(x, y, 1.5, 0, Math.PI * 2);
    ctx.fill();
  }

  // Highlight key landmarks (nose, eyes, ears, forehead, chin, mouth corners)
  const highlight = [1, 33, 263, 234, 454, 10, 152, 61, 291];
  ctx.fillStyle = "rgba(255,220,0,0.9)";
  for (const idx of highlight) {
    if (!landmarks[idx]) continue;
    const x = landmarks[idx].x * canvas.width;
    const y = landmarks[idx].y * canvas.height;
    ctx.beginPath();
    ctx.arc(x, y, 3, 0, Math.PI * 2);
    ctx.fill();
  }

  // Draw pose skeletons for secondary persons (red)
  if (poseLandmarks && poseLandmarks.length > 1) {
    ctx.strokeStyle = "rgba(255,50,50,0.8)";
    ctx.lineWidth = 2;

    // Draw pose connections for all secondary persons (skip first, that's the candidate)
    const POSE_CONNECTIONS = [
      [11, 12], [11, 13], [13, 15], [12, 14], [14, 16], // Arms
      [11, 23], [12, 24], [23, 24], // Torso
      [23, 25], [24, 26], [25, 27], [26, 28], // Legs
      [27, 29], [28, 30], [29, 31], [30, 32] // Feet
    ];

    for (let i = 1; i < poseLandmarks.length; i++) {
      const person = poseLandmarks[i];
      for (const [start, end] of POSE_CONNECTIONS) {
        const p1 = person[start];
        const p2 = person[end];
        if (p1 && p2 && p1.x > 0 && p2.x > 0) {
          ctx.beginPath();
          ctx.moveTo(p1.x * canvas.width, p1.y * canvas.height);
          ctx.lineTo(p2.x * canvas.width, p2.y * canvas.height);
          ctx.stroke();
        }
      }
    }
  }

  // Pose label with colour coding
  const poseColor =
    pose === "front"
      ? "#4caf50"
      : pose === "left"
        ? "#2196f3"
        : pose === "right"
          ? "#ff9800"
          : "#aaa";
  ctx.font = `bold ${Math.round(canvas.height * 0.045)}px sans-serif`;
  ctx.fillStyle = poseColor;
  ctx.textAlign = "left";
  ctx.fillText(
    `${pose.toUpperCase()} (${yawDeg.toFixed(1)}°)`,
    8,
    Math.round(canvas.height * 0.055)
  );
}

// ── Classify pose from smoothed yaw ─────────────────────────────────────────
function classifyPose(yawDeg: number): HeadPose {
  if (yawDeg > YAW_THRESHOLD) return "right";
  if (yawDeg < -YAW_THRESHOLD) return "left";
  return "front";
}

// ═══════════════════════════════════════════════════════════════════════════
// Hook
// ═══════════════════════════════════════════════════════════════════════════
export function useMediaPipe(videoRef: VideoRef, enabled: boolean = true) {
  const landmarkerRef = useRef<FaceLandmarker | null>(null);
  const poseLandmarkerRef = useRef<PoseLandmarker | null>(null);
  const initPromiseRef = useRef<Promise<void> | null>(null);
  const landmarkCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const analysisCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const bgMotionCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const spoofCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const cameraBlockCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const prevFrameCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const captureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const captureAnalysisCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // Throttling counters
  const frameCountRef = useRef(0);

  // --- Model state ---
  const [modelReady, setModelReady] = useState(false);
  const [modelError, setModelError] = useState<string | null>(null);
  const lastFaceStatusRef = useRef<FaceStatus | null>(null);

  // ── Resource pool initialization ──────────────────────────────────────────
  useEffect(() => {
    landmarkCanvasRef.current = document.createElement("canvas");
    analysisCanvasRef.current = document.createElement("canvas");
    bgMotionCanvasRef.current = document.createElement("canvas");
    spoofCanvasRef.current = document.createElement("canvas");
    cameraBlockCanvasRef.current = document.createElement("canvas");
    prevFrameCanvasRef.current = document.createElement("canvas");
    captureCanvasRef.current = document.createElement("canvas");
    captureAnalysisCanvasRef.current = document.createElement("canvas");
  }, []);

  // --- Face status (public) ---
  const [faceStatus, setFaceStatus] = useState<FaceStatus>({
    count: 0,
    detected: false,
    pose: "unknown",
    yawDeg: 0,
    landmarkCanvas: null,
    flags: [],
    attentionScore: 100,
    lastSeenAt: 0,
    lookAwayDuration: 0,
    noFaceDuration: 0,
    backgroundMotion: 0,
    imageVariance: 1,
    faceOcclusion: 0,
    personCount: 0,
    blinkCount: 0,
    lastBlinkAt: 0,
    eyeAspectRatio: 1,
    spoofConfidence: "high",
    cameraBlocked: false,
  });

  // --- Internal tracking refs (mutable, no re-renders) ---
  const smoothedYawRef = useRef(0);
  const lastSeenRef = useRef(0);
  const lastFrameTimeRef = useRef(0);
  const lookAwayAccRef = useRef(0);
  const noFaceAccRef = useRef(0);
  const prevFrameDataRef = useRef<ImageData | null>(null);
  const backgroundMotionAccRef = useRef(0);
  const loopRef = useRef<{ raf: number; timeout: ReturnType<typeof setTimeout> | null }>({
    raf: 0,
    timeout: null,
  });
  const mountedRef = useRef(true);

  // Eye blink tracking
  const blinkCountRef = useRef(0);
  const lastBlinkAtRef = useRef(0);
  const blinkResetTimeRef = useRef(Date.now());
  const earHistoryRef = useRef<number[]>([]);

  // Spoof detection tracking
  const lastSpoofCheckRef = useRef(0);
  const spoofCheckIntervalMs = 5000; // Check every 5 seconds

  // Camera block detection
  const staticFrameCountRef = useRef(0);
  const lastFrameHashRef = useRef<string>("");

  // Head movement buffering and cooldown
  const headMovementBufferRef = useRef<number[]>([]);
  const lastHeadMovementWarningRef = useRef(0);

  // Face leaving frame buffer
  const faceLeavingBufferRef = useRef<number[]>([]);
  const lastFaceLeavingWarningRef = useRef(0);

  // Camera inactive tracking (Camera turned off)
  const cameraInactiveAccRef = useRef(0);

  // ── Model initialisation ──────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;
    mountedRef.current = true;
    let cancelled = false;

    initPromiseRef.current = (async () => {
      try {
        const vision = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
        );

        // Load Face Landmarker
        const faceOpts = {
          baseOptions: {
            modelAssetPath:
              "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
            delegate: "GPU" as const,
          },
          runningMode: "VIDEO" as const,
          numFaces: 5, // detect up to 5 faces to catch people in background
          outputFaceBlendshapes: false,
          outputFacialTransformationMatrixes: false,
        };

        let landmarker: FaceLandmarker | null = null;
        try {
          landmarker = await FaceLandmarker.createFromOptions(vision, faceOpts);
        } catch {
          console.warn("GPU delegate failed for FaceLandmarker, falling back to CPU");
          landmarker = await FaceLandmarker.createFromOptions(vision, {
            ...faceOpts,
            baseOptions: { ...faceOpts.baseOptions, delegate: "CPU" },
          });
        }

        // Load Pose Landmarker
        const poseOpts = {
          baseOptions: {
            modelAssetPath:
              "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task",
            delegate: "GPU" as const,
          },
          runningMode: "VIDEO" as const,
          numPoses: 3, // detect up to 3 people
        };

        let poseLandmarker: PoseLandmarker | null = null;
        try {
          poseLandmarker = await PoseLandmarker.createFromOptions(vision, poseOpts);
        } catch {
          console.warn("GPU delegate failed for PoseLandmarker, falling back to CPU");
          poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
            ...poseOpts,
            baseOptions: { ...poseOpts.baseOptions, delegate: "CPU" },
          });
        }

        if (!cancelled) {
          landmarkerRef.current = landmarker;
          poseLandmarkerRef.current = poseLandmarker;
          setModelReady(true);
        }
      } catch (err: any) {
        if (!cancelled) {
          console.error("MediaPipe init failed:", err);
          setModelError(err.message || "Failed to load proctoring models");
        }
      }
    })();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      // Clean up detection loop
      cancelAnimationFrame(loopRef.current.raf);
      if (loopRef.current.timeout) clearTimeout(loopRef.current.timeout);
      landmarkerRef.current?.close();
      landmarkerRef.current = null;
      poseLandmarkerRef.current?.close();
      poseLandmarkerRef.current = null;
    };
  }, []);

  // ── Throttled detection loop (~10fps) ─────────────────────────────────────
  useEffect(() => {
    if (!enabled || !modelReady) return;

    const scheduleNext = () => {
      // Use setTimeout to throttle to TARGET_FPS, then rAF for vsync alignment
      loopRef.current.timeout = setTimeout(() => {
        if (!mountedRef.current) return;
        loopRef.current.raf = requestAnimationFrame(detect);
      }, FRAME_INTERVAL_MS);
    };

    const detect = () => {
      if (!mountedRef.current) return;

      const video = videoRef.current;
      const landmarker = landmarkerRef.current;
      const poseLandmarker = poseLandmarkerRef.current;

      if (!video || !landmarker || !poseLandmarker || video.readyState < 2) {
        if (video && video.readyState < 2) {
          cameraInactiveAccRef.current += FRAME_INTERVAL_MS;
          if (cameraInactiveAccRef.current > 2000) {
            setFaceStatus(prev => {
              const flags = prev.flags.includes("camera_blocked") ? prev.flags : [...prev.flags, "camera_blocked"];
              return { ...prev, cameraBlocked: true, flags };
            });
          }
        }
        scheduleNext();
        return;
      }

      // Reset inactive counter when camera is working
      cameraInactiveAccRef.current = 0;

      try {
        const now = performance.now();
        const dt = now - (lastFrameTimeRef.current || now);
        lastFrameTimeRef.current = now;
        frameCountRef.current++;

        // Ensure canvases are available
        if (!analysisCanvasRef.current || !bgMotionCanvasRef.current ||
          !spoofCanvasRef.current || !cameraBlockCanvasRef.current ||
          !prevFrameCanvasRef.current || !landmarkCanvasRef.current) {
          scheduleNext();
          return;
        }

        // Run face detection
        const result = landmarker.detectForVideo(video, now);
        const rawCount = result.faceLandmarks?.length ?? 0;

        // Run pose detection (for body/person count)
        const poseResult = poseLandmarker.detectForVideo(video, now);
        const personCount = poseResult.landmarks?.length ?? 0;

        // ── Yaw smoothing & pose ──
        let smoothedYaw = smoothedYawRef.current;
        let pose: HeadPose = "unknown";
        let spoofConfidence: "low" | "medium" | "high" = lastFaceStatusRef.current?.spoofConfidence || "high";
        let isSpoofed = lastFaceStatusRef.current?.flags.includes("possible_spoof_flat_image") || false;
        let currentEAR = 1;

        if (rawCount === 1) {
          const rawYaw = computeRawYaw(result.faceLandmarks[0]);
          smoothedYaw = YAW_SMOOTHING * rawYaw + (1 - YAW_SMOOTHING) * smoothedYawRef.current;
          smoothedYawRef.current = smoothedYaw;
          pose = classifyPose(smoothedYaw);

          drawLandmarks(
            landmarkCanvasRef.current,
            video,
            result.faceLandmarks[0],
            smoothedYaw,
            pose,
            poseResult.landmarks
          );

          // Spoof detection (throttled to every ~5 seconds / 50 frames)
          if (frameCountRef.current % 50 === 0) {
            const spoofResult = checkForSpoofing(video, result.faceLandmarks[0], spoofCanvasRef.current);
            spoofConfidence = spoofResult.confidence;
            isSpoofed = spoofResult.isSpoofed;
          }

          // Eye blink detection
          const blinkResult = detectBlink(
            result.faceLandmarks[0],
            earHistoryRef,
            blinkCountRef,
            lastBlinkAtRef,
            blinkResetTimeRef,
            now
          );
          currentEAR = blinkResult.ear;
        } else {
          smoothedYawRef.current *= 0.9;
          smoothedYaw = smoothedYawRef.current;
          const ctx = landmarkCanvasRef.current.getContext("2d");
          ctx?.clearRect(0, 0, landmarkCanvasRef.current.width, landmarkCanvasRef.current.height);
        }

        // ── Analysis & Flags ──
        if (rawCount >= 1) lastSeenRef.current = now;
        const timeSinceLastSeen = now - lastSeenRef.current;
        const debouncedDetected = timeSinceLastSeen < FACE_DEBOUNCE_MS;

        const flags: string[] = [];
        if (rawCount > 1) flags.push("multiple_faces");
        if (isSpoofed) flags.push("possible_spoof_flat_image");

        const timeSinceLastBlink = now - lastBlinkAtRef.current;
        const shouldFlagNoBlink = rawCount === 1 && timeSinceLastBlink > 15000 && spoofConfidence === "low";
        if (shouldFlagNoBlink) flags.push("no_blink_detected");

        // Accumulators
        if (!debouncedDetected) {
          noFaceAccRef.current += dt;
          faceLeavingBufferRef.current.push(1);
        } else {
          noFaceAccRef.current = Math.max(0, noFaceAccRef.current - dt * 2);
          faceLeavingBufferRef.current = [];
        }
        if (faceLeavingBufferRef.current.length > HEAD_MOVEMENT_BUFFER_FRAMES) faceLeavingBufferRef.current.shift();

        if (noFaceAccRef.current > NO_FACE_THRESHOLD_MS &&
          faceLeavingBufferRef.current.length >= HEAD_MOVEMENT_BUFFER_FRAMES &&
          faceLeavingBufferRef.current.every(f => f === 1) &&
          now - lastFaceLeavingWarningRef.current > HEAD_MOVEMENT_COOLDOWN_MS) {
          flags.push("no_face");
          lastFaceLeavingWarningRef.current = now;
        }

        const isAway = pose !== "front" && Math.abs(smoothedYaw) > 30;
        if (rawCount === 1 && isAway) {
          lookAwayAccRef.current += dt;
          headMovementBufferRef.current.push(1);
        } else if (rawCount === 1 && pose === "front") {
          lookAwayAccRef.current = Math.max(0, lookAwayAccRef.current - dt * 2);
          headMovementBufferRef.current = [];
        } else {
          lookAwayAccRef.current = 0;
          headMovementBufferRef.current = [];
        }
        if (headMovementBufferRef.current.length > HEAD_MOVEMENT_BUFFER_FRAMES) headMovementBufferRef.current.shift();

        if (lookAwayAccRef.current > LOOKING_AWAY_THRESHOLD_MS &&
          headMovementBufferRef.current.length >= HEAD_MOVEMENT_BUFFER_FRAMES &&
          headMovementBufferRef.current.every(f => f === 1) &&
          now - lastHeadMovementWarningRef.current > HEAD_MOVEMENT_COOLDOWN_MS) {
          flags.push("looking_away");
          lastHeadMovementWarningRef.current = now;
        }

        // ── Frame Analysis (Pooled) ──
        const frameAnalysis = analyzeFrame(video, prevFrameDataRef.current, analysisCanvasRef.current);

        // Update prev frame data (Pooled)
        const pfCanvas = prevFrameCanvasRef.current;
        pfCanvas.width = 64; pfCanvas.height = 64;
        const pfCtx = pfCanvas.getContext("2d", { alpha: false, desynchronized: true });
        if (pfCtx) {
          pfCtx.drawImage(video, (video.videoWidth - 64) / 2, (video.videoHeight - 64) / 2, 64, 64, 0, 0, 64, 64);
          prevFrameDataRef.current = pfCtx.getImageData(0, 0, 64, 64);
        }

        if (frameAnalysis.brightness < MIN_BRIGHTNESS) flags.push("too_dark");
        if (frameAnalysis.variance < MIN_VARIANCE) flags.push("camera_obstructed");

        const cameraBlockResult = checkCameraBlock(
          video, frameAnalysis.brightness, rawCount,
          lastFrameHashRef, staticFrameCountRef, dt, cameraBlockCanvasRef.current
        );
        if (cameraBlockResult.isBlocked) flags.push("camera_blocked");
        if (cameraBlockResult.isStatic) flags.push("camera_static_or_covered");

        // Face occlusion (phone coverage)
        let faceOcclusion = lastFaceStatusRef.current?.faceOcclusion || 0;
        if (rawCount >= 1 && result.faceLandmarks[0]) {
          faceOcclusion = estimateFaceOcclusion(result.faceLandmarks[0]);
          if (faceOcclusion > OCCLUSION_THRESHOLD) flags.push("phone_coverage_detected");
        }

        // Background motion (throttled to every 5th frame)
        let bgMotion = lastFaceStatusRef.current?.backgroundMotion || 0;
        if (frameCountRef.current % 5 === 0) {
          bgMotion = analyzeBackgroundMotion(video, prevFrameDataRef.current, rawCount >= 1 ? result.faceLandmarks[0] : null, bgMotionCanvasRef.current);
        }

        if (bgMotion > MOTION_THRESHOLD) {
          backgroundMotionAccRef.current += dt;
        } else {
          backgroundMotionAccRef.current = Math.max(0, backgroundMotionAccRef.current - dt * 3);
        }
        if (backgroundMotionAccRef.current > 2000) flags.push("background_movement");

        if (rawCount > 1 || (rawCount === 1 && bgMotion > MOTION_THRESHOLD * 1.5)) flags.push("multiple_people_detected");
        if (personCount > 1 && rawCount === 1) flags.push("person_in_background");
        if (personCount > 1 && rawCount > 1) flags.push("multiple_people_with_faces");

        // Attention score
        let attention = 100;
        if (noFaceAccRef.current > 0) attention -= Math.min(50, (noFaceAccRef.current / NO_FACE_THRESHOLD_MS) * 50);
        if (lookAwayAccRef.current > 0) attention -= Math.min(30, (lookAwayAccRef.current / LOOKING_AWAY_THRESHOLD_MS) * 30);
        if (rawCount > 1) attention -= 20;
        if (frameAnalysis.variance < MIN_VARIANCE) attention -= 15;
        if (faceOcclusion > OCCLUSION_THRESHOLD) attention -= 10;
        if (bgMotion > MOTION_THRESHOLD) attention -= Math.min(15, bgMotion / 10);
        attention = Math.max(0, Math.round(attention));

        const nextStatus = {
          count: rawCount, detected: debouncedDetected, pose, yawDeg: smoothedYaw,
          landmarkCanvas: landmarkCanvasRef.current, flags, attentionScore: attention,
          lastSeenAt: lastSeenRef.current, lookAwayDuration: lookAwayAccRef.current,
          noFaceDuration: noFaceAccRef.current, backgroundMotion: bgMotion,
          imageVariance: frameAnalysis.variance, faceOcclusion, personCount,
          blinkCount: blinkCountRef.current, lastBlinkAt: lastBlinkAtRef.current,
          eyeAspectRatio: currentEAR, spoofConfidence,
          cameraBlocked: cameraBlockResult.isBlocked || cameraBlockResult.isStatic,
        };

        // Batch updates: only setState if anything meaningful changed
        const hasChanged = !lastFaceStatusRef.current ||
          lastFaceStatusRef.current.count !== nextStatus.count ||
          lastFaceStatusRef.current.pose !== nextStatus.pose ||
          lastFaceStatusRef.current.attentionScore !== nextStatus.attentionScore ||
          lastFaceStatusRef.current.flags.length !== nextStatus.flags.length ||
          Math.abs(lastFaceStatusRef.current.yawDeg - nextStatus.yawDeg) > 2 ||
          nextStatus.flags.length !== lastFaceStatusRef.current.flags.length ||
          nextStatus.flags.some((f, i) => f !== lastFaceStatusRef.current?.flags[i]);

        if (hasChanged) {
          setFaceStatus(nextStatus);
          lastFaceStatusRef.current = nextStatus;
        }

      } catch (err) {
        // Detection can throw on bad frames — log once and continue
        console.warn("Detection frame error (non-fatal):", err);
      }

      scheduleNext();
    };

    // Kick off the loop
    loopRef.current.raf = requestAnimationFrame(detect);

    return () => {
      cancelAnimationFrame(loopRef.current.raf);
      if (loopRef.current.timeout) clearTimeout(loopRef.current.timeout);
    };
  }, [modelReady, videoRef]);

  // ── Capture with quality checks ───────────────────────────────────────────
  /**
   * Capture the current video frame as a high-quality JPEG Blob.
   *
   * @param requireFace  If true (default), rejects when 0 or 2+ faces found.
   *                     Pass false for document captures (e.g. ID card).
   * @returns blob, faceCount, and a rejectReason if capture failed.
   */
  const captureFrame = useCallback(
    async (requireFace = true): Promise<CaptureResult> => {
      await initPromiseRef.current;

      const video = videoRef.current;
      const landmarker = landmarkerRef.current;
      if (!video || !landmarker || video.readyState < 2) {
        return { blob: null, faceCount: 0, rejectReason: "not_ready" };
      }

      // Run detection for this exact frame
      const result = landmarker.detectForVideo(video, performance.now());
      const faceCount = result.faceLandmarks?.length ?? 0;

      if (requireFace && faceCount === 0) {
        return { blob: null, faceCount, rejectReason: "no_face" };
      }
      if (requireFace && faceCount > 1) {
        return { blob: null, faceCount, rejectReason: "multiple_faces" };
      }

      // Brightness and quality check — reject frames that are too dark or obstructed
      const frameAnalysis = analyzeFrame(video, null, captureAnalysisCanvasRef.current!);
      if (requireFace && frameAnalysis.brightness < MIN_BRIGHTNESS) {
        return { blob: null, faceCount, rejectReason: "too_dark" };
      }

      // Draw frame at native video resolution
      const canvas = captureCanvasRef.current!;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d", { alpha: false, desynchronized: true });
      if (!ctx) return { blob: null, faceCount, rejectReason: null };
      ctx.drawImage(video, 0, 0);

      // Encode at high quality for AWS Rekognition
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), "image/jpeg", CAPTURE_JPEG_QUALITY);
      });

      return { blob, faceCount, rejectReason: null };
    },
    [videoRef]
  );

  return { captureFrame, modelReady, modelError, faceStatus };
}
